/* eslint-disable no-console */
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { safeStorage } from 'common/utils/safeStorage';

/**
 * A guest can pay through the App Store / Play before they have an account.
 * If they close the app between paying and signing up, they have paid and
 * have nothing to log into, and the store receipt lives under an anonymous
 * RevenueCat id nobody can look up by email. This flag remembers that a
 * purchase is waiting for an account so the app can keep steering them into
 * sign-up on every launch until it happens.
 *
 * Native uses Capacitor Preferences (survives WebView storage purges);
 * web falls back to localStorage. Cleared the moment an account is verified.
 */
export const PENDING_GUEST_PURCHASE_KEY = 'doqit.pendingGuestPurchase';
const DISMISSED_KEY = 'doqit.pendingGuestPurchase.dismissed';

export type PendingGuestPurchase = {
  rcAnonymousId: string | null;
  purchasedAt: string;
};

const RC_ANONYMOUS_PREFIX = '$RCAnonymousID:';

/**
 * The current RevenueCat app user id, but only when it is one of the SDK's
 * own anonymous ids. A previous account's identified id lingering on a
 * shared device is never returned, so it can never be linked to a different
 * account. Web keeps its anonymous id in localStorage (see app.tsx boot).
 */
export async function getAnonymousRcAppUserId(): Promise<string | null> {
  try {
    if (Capacitor.isNativePlatform()) {
      const { appUserID } = await Purchases.getAppUserID();
      return appUserID?.startsWith(RC_ANONYMOUS_PREFIX) ? appUserID : null;
    }
    // Same rule on web: a legacy custom-format id stored by an older build is
    // treated as identified by RevenueCat and must never be sent for linking.
    const stored = safeStorage.getItem('rc_anonymous_id');
    return stored?.startsWith(RC_ANONYMOUS_PREFIX) ? stored : null;
  } catch {
    return null;
  }
}

const readRaw = async (): Promise<string | null> => {
  try {
    if (Capacitor.isNativePlatform()) {
      const { value } = await Preferences.get({ key: PENDING_GUEST_PURCHASE_KEY });
      return value ?? null;
    }
  } catch (error) {
    console.warn('[guest-purchase] Preferences read failed', error);
  }
  return safeStorage.getItem(PENDING_GUEST_PURCHASE_KEY);
};

export async function getPendingGuestPurchase(): Promise<PendingGuestPurchase | null> {
  const raw = await readRaw();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingGuestPurchase>;
    if (!parsed || typeof parsed.purchasedAt !== 'string') return null;
    return { rcAnonymousId: parsed.rcAnonymousId ?? null, purchasedAt: parsed.purchasedAt };
  } catch {
    return null;
  }
}

/**
 * Call right after a purchase or restore succeeds for someone with no
 * account. Best-effort: a storage failure must never get in the way of the
 * navigation into sign-up that follows it.
 */
export async function markPendingGuestPurchase(): Promise<void> {
  const record: PendingGuestPurchase = {
    rcAnonymousId: await getAnonymousRcAppUserId(),
    purchasedAt: new Date().toISOString(),
  };
  const raw = JSON.stringify(record);
  try {
    if (Capacitor.isNativePlatform()) {
      await Preferences.set({ key: PENDING_GUEST_PURCHASE_KEY, value: raw });
    }
  } catch (error) {
    console.warn('[guest-purchase] Preferences write failed', error);
  }
  // Written to localStorage on every platform too, so a synchronous read is
  // possible where an async one would be awkward (and as the web store).
  safeStorage.setItem(PENDING_GUEST_PURCHASE_KEY, raw);
  try {
    sessionStorage.removeItem(DISMISSED_KEY);
  } catch {
    // Session storage unavailable; the banner simply shows.
  }
}

export async function clearPendingGuestPurchase(): Promise<void> {
  try {
    if (Capacitor.isNativePlatform()) {
      await Preferences.remove({ key: PENDING_GUEST_PURCHASE_KEY });
    }
  } catch (error) {
    console.warn('[guest-purchase] Preferences remove failed', error);
  }
  safeStorage.removeItem(PENDING_GUEST_PURCHASE_KEY);
}

/** The banner can be dismissed for the rest of this app session only. */
export const isPendingGuestPurchaseBannerDismissed = (): boolean => {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
};

export const dismissPendingGuestPurchaseBanner = (): void => {
  try {
    sessionStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    // Session storage unavailable; the banner will show again next mount.
  }
};
